import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { TFunction } from 'i18next';
import {
  CheckLabelCell,
  CheckSelectCell,
  CoreStackCell,
  DisplayCell,
  DueDateCell,
  EmailFieldCell,
  GradingStackCell,
  GroupHeader,
  PlainSelectCell,
  ProductNumberCell,
  ProductNumberGroupHeader,
  RsSupplierCell,
  SampleCell,
  SampleTypeCell,
  TextInputCell,
  UomActionsCell,
} from './cells/cellRenderers';
import { SHIPMENT_METHOD_OPTIONS } from './costingOptions';
import { getLengthLabel } from '../../../../Components/helper';
import type { ArrKey, BoolKey, CoreKey, CoreMultiKey, CtxOptionsKey, MultiSubmitRow, OptionsKey, SelectOption, StrKey } from './types';
import { checkedListSpec, flagSpec, hasCostingSupplier, optionsFrom, sampleTypeSpec, selectSpec, textSpec, toIsoDate, toNumericText, withFill } from './cellFill';
import { coreSpec, gradingSpec } from './cellFillStack';

export const REQUEST_SAMPLE_GROUP_ID = 'requestSample';
export const CORE_PRODUCT_GROUP_ID = 'core';

/** Every column group that can expand/collapse (used to open them all together). */
export const EXPANDABLE_GROUP_IDS = ['tp', 'sd', REQUEST_SAMPLE_GROUP_ID, 'ci', CORE_PRODUCT_GROUP_ID, 'email'];

/** Cell classes for a multi-select column narrow enough that its menu needs a widened min/max. */
const NARROW_MS_CELL = 'mssg-ms-cell mssg-ms-cell--narrow mssg-cell-top';

type ColumnDef = ColDef<MultiSubmitRow> | ColGroupDef<MultiSubmitRow>;

/**
 * Build the full column-group structure. The grid hides the leaf-header row
 * (headerHeight=0); the only visible header is the coloured group row, so every
 * top-level column (Product Number, Actions) is wrapped in its own group.
 */
export function buildColumnDefs(t: TFunction): ColumnDef[] {
  return [
    {
      headerName: t('GROUPS.PRODUCT_NUMBER'),
      groupId: 'product',
      headerClass: 'mssg-grp mssg-grp--product',
      headerGroupComponent: ProductNumberGroupHeader,
      marryChildren: true,
      children: [
        {
          headerName: '',
          colId: 'product-number',
          field: 'productNumber',
          sortable: true,
          cellRenderer: ProductNumberCell,
          pinned: 'left',
          width: 240,
        },
      ],
    },

    {
      headerName: t('GROUPS.COLOURWAY'),
      groupId: 'colourway',
      headerClass: 'mssg-grp mssg-grp--cw',
      headerGroupComponent: GroupHeader,
      headerGroupComponentParams: { showCheckbox: true },
      marryChildren: true,
      children: [
        withFill(
          {
            headerName: '',
            colId: 'product-colourway',
            cellClass: 'mssg-ms-cell',
            width: 200,
            cellRenderer: CheckSelectCell,
            cellRendererParams: {
              checkedField: 'cwChecked',
              valueField: 'productColourways',
              optionsKey: 'colourwayOptions',
              placeholder: t('GROUPS.COLOURWAY'),
            },
          },
          checkedListSpec('cwChecked', 'productColourways', 'colourwayOptions'),
        ),
      ],
    },

    {
      headerName: t('GROUPS.COSTING_PURCHASING'),
      groupId: 'cp',
      headerClass: 'mssg-grp mssg-grp--cp',
      headerGroupComponent: GroupHeader,
      headerGroupComponentParams: { showCheckbox: true },
      marryChildren: true,
      children: [
        withFill(
          {
            headerName: '',
            colId: 'cp-po',
            flex: 1,
            minWidth: 180,
            cellClass: 'mssg-ms-cell',
            cellRenderer: CheckSelectCell,
            cellRendererParams: {
              checkedField: 'cpChecked',
              valueField: 'purchaseOrders',
              optionsKey: 'poOptions',
              placeholder: t('CP.PURCHASE_ORDERS'),
            },
          },
          checkedListSpec('cpChecked', 'purchaseOrders', 'poOptions'),
        ),
      ],
    },

    {
      headerName: t('GROUPS.TECHNICAL_PACKAGE'),
      groupId: 'tp',
      headerClass: 'mssg-grp mssg-grp--tp',
      headerGroupComponent: GroupHeader,
      headerGroupComponentParams: {
        showCheckbox: true,
        note: t('TP.COMBINE_NOTE'),
      },
      marryChildren: true,
      children: [
        checkLabel('tp-design', 'tpDesignDetails', t('TP.DESIGN_DETAILS'), undefined, 180, true),
        checkLabel('tp-bom', 'tpBom', t('TP.BILL_OF_MATERIALS'), 'open'),
        withFill(
          {
            headerName: '',
            colId: 'tp-spec',
            width: 190,
            columnGroupShow: 'open',
            cellClass: 'mssg-ms-cell',
            cellRenderer: CheckSelectCell,
            cellRendererParams: {
              checkedField: 'tpSpecification',
              valueField: 'tpSpecificationValue',
              optionsKey: 'gradingGroups',
              placeholder: t('TP.SPECIFICATIONS'),
            },
          },
          checkedListSpec('tpSpecification', 'tpSpecificationValue', 'gradingGroups'),
        ),
        checkLabel('tp-testing', 'tpTesting', t('TP.TESTING_REQUIREMENTS'), 'open'),
        checkLabel('tp-care', 'tpCare', t('TP.CARE_USER_INSTRUCTIONS'), 'open'),
        withFill(
          {
            headerName: '',
            colId: 'tp-sample',
            width: 220,
            columnGroupShow: 'open',
            cellClass: 'mssg-ms-cell',
            cellRenderer: SampleCell,
          },
          checkedListSpec('tpSample', 'tpSampleValue', 'sampleOptions'),
        ),
      ],
    },

    {
      headerName: t('GROUPS.SUPPORTING_DOCUMENTS'),
      groupId: 'sd',
      headerClass: 'mssg-grp mssg-grp--sd',
      headerGroupComponent: GroupHeader,
      headerGroupComponentParams: { showCheckbox: true },
      marryChildren: true,
      children: [
        supportingDoc(t('SD.DESIGN_DETAILS'), 'sdDesignDetails', 'sdDesignDetailsValue', 'designDocs', undefined, 190, true),
        supportingDoc(t('SD.COLOUR'), 'sdColour', 'sdColourValue', 'colourDocs', 'open'),
        supportingDoc(t('SD.BILL_OF_MATERIALS'), 'sdBom', 'sdBomValue', 'bomDocs', 'open'),
        supportingDoc(t('SD.SPECIFICATION'), 'sdSpecification', 'sdSpecificationValue', 'otherDocs', 'open'),
        supportingDoc(t('SD.POM_GUIDE'), 'sdPomGuide', 'sdPomGuideValue', 'gradingGroups', 'open'),
        supportingDoc(t('SD.PURCHASE_ORDER'), 'sdPurchaseOrder', 'sdPurchaseOrderValue', 'poDocs', 'open'),
        sdCheckLabel('sd-testing', 'sdTestingReport', t('SD.TESTING_REPORT'), 'open'),
        sdCheckLabel('sd-care', 'sdCareLayout', t('SD.CARE_USER_LAYOUT'), 'open'),
      ],
    },

    {
      headerName: t('GROUPS.REQUEST_SAMPLE'),
      groupId: REQUEST_SAMPLE_GROUP_ID,
      headerClass: 'mssg-grp mssg-grp--rs',
      headerGroupComponent: GroupHeader,
      headerGroupComponentParams: { showCheckbox: false },
      marryChildren: true,
      children: [
        withFill(
          {
            headerName: '',
            colId: 'rs-sampleTypeGroup',
            flex: 1,
            minWidth: 200,
            cellRenderer: PlainSelectCell,
            cellRendererParams: {
              valueField: 'sampleTypeGroup',
              optionsKey: 'sampleTypeGroupOptions',
              placeholder: t('RS.SAMPLE_TYPE_GROUP'),
              isSampleTypeGroup: true,
            },
          },
          selectSpec('sampleTypeGroup', optionsFrom({ optionsKey: 'sampleTypeGroupOptions' }), (ctx, rowId, value) => ctx.setSampleTypeGroup(rowId, value)),
        ),
        withFill(
          {
            headerName: '',
            colId: 'rs-sampleType',
            width: 160,
            columnGroupShow: 'open',
            cellRenderer: SampleTypeCell,
            cellRendererParams: { placeholder: t('RS.SAMPLE_TYPE') },
          },
          sampleTypeSpec(),
        ),
        withFill(
          {
            headerName: '',
            colId: 'rs-rsSupplier',
            width: 150,
            columnGroupShow: 'open',
            cellRenderer: RsSupplierCell,
            cellRendererParams: { placeholder: t('RS.SUPPLIER') },
          },
          selectSpec('rsSupplier', optionsFrom({ ctxOptionsKey: 'supplierOptions' })),
        ),
        withFill(
          {
            headerName: '',
            colId: 'rs-due',
            width: 150,
            columnGroupShow: 'open',
            cellRenderer: DueDateCell,
          },
          textSpec('dueDate', toIsoDate),
        ),
        withFill(
          {
            headerName: '',
            colId: 'rs-factory',
            width: 150,
            columnGroupShow: 'open',
            cellRenderer: PlainSelectCell,
            cellRendererParams: {
              valueField: 'factory',
              ctxOptionsKey: 'factoryOptions',
              placeholder: t('RS.FACTORY'),
            },
          },
          selectSpec('factory', optionsFrom({ ctxOptionsKey: 'factoryOptions' })),
        ),
        withFill(
          {
            headerName: '',
            colId: 'rs-colourway',
            width: 150,
            columnGroupShow: 'open',
            cellRenderer: PlainSelectCell,
            cellRendererParams: {
              valueField: 'colourway',
              optionsKey: 'colourwayOptions',
              placeholder: t('RS.COLOURWAY'),
            },
          },
          selectSpec('colourway', optionsFrom({ optionsKey: 'colourwayOptions' })),
        ),
        gradingCol(t('RS.GRADING_GROUP'), 'gradingGroup', 'select', undefined, 160, 'gradingGroups'),
        gradingCol(t('RS.SIZE'), 'size', 'select', undefined, 110, undefined, undefined, 'mssg-ms-cell mssg-ms-cell--narrow mssg-cell-top'),
        gradingCol(getLengthLabel(t('RS.LENGTH')), 'length', 'select', undefined, 120, 'legLengthOptions'),
        gradingCol(t('RS.QUANTITY'), 'quantity', 'input', undefined, 110),
        withFill(
          {
            headerName: '',
            colId: 'rs-grading-uom',
            width: 180,
            columnGroupShow: 'open',
            cellClass: 'mssg-cell-top',
            cellRenderer: UomActionsCell,
            cellRendererParams: { ctxOptionsKey: 'unitOptions' },
          },
          gradingSpec('uom', { ctxOptionsKey: 'unitOptions' }),
        ),
      ],
    },

    {
      headerName: t('GROUPS.COSTING_INFORMATION'),
      groupId: 'ci',
      headerClass: 'mssg-grp mssg-grp--ci',
      headerGroupComponent: GroupHeader,
      headerGroupComponentParams: { showCheckbox: false },
      marryChildren: true,
      children: [
        ciText('ci-reference', 'ciReference', t('CI.REFERENCE'), undefined, 180, true),
        ciSelect('ci-supplier', 'ciSupplier', t('CI.SUPPLIER'), { ctxOptionsKey: 'costingSupplierOptions' }, 170, false),
        ciSelect('ci-factory', 'ciFactory', t('CI.FACTORY'), { optionsKey: 'ciFactoryOptions' }),
        ciSelect('ci-template', 'ciCostingTemplate', t('CI.COSTING_TEMPLATE'), { optionsKey: 'costingTemplateOptions' }, 180),
        ciDisplay('ci-currency', 'currency', t('CI.CURRENCY'), 150),
        ciText('ci-moq', 'ciMoqNeeded', t('CI.MOQ_NEEDED'), 'open', 140, false, true),
        ciText('ci-target-cost', 'ciTargetCost', t('CI.TARGET_COST'), 'open', 150, false, true),
        ciSelect('ci-incoterms', 'ciIncoterms', t('CI.INCOTERMS'), { ctxOptionsKey: 'incotermsOptions' }, 160),
        ciSelect('ci-shipment', 'ciShipmentMethod', t('CI.SHIPMENT_METHOD'), { options: SHIPMENT_METHOD_OPTIONS }, 170),
        ciSelect('ci-sample-ref', 'ciSampleRef', t('CI.SAMPLE_REF'), { optionsKey: 'sampleRefOptions' }, 160),
      ],
    },

    {
      headerName: t('GROUPS.CORE_PRODUCT'),
      groupId: CORE_PRODUCT_GROUP_ID,
      headerClass: 'mssg-grp mssg-grp--core',
      headerGroupComponent: GroupHeader,
      headerGroupComponentParams: { showCheckbox: false },
      marryChildren: true,
      children: [
        coreSelect('core-colourway', 'colourway', t('CORE.COLOURWAY'), { optionsKey: 'coreColourwayOptions' }, undefined, 180, true),
        coreSelect('core-size', 'size', t('CORE.SIZE'), { optionsKey: 'coreSizeOptions' }, 'open', 120, false, NARROW_MS_CELL),
        coreSelect('core-length', 'length', getLengthLabel(t('CORE.LENGTH')), { optionsKey: 'coreLengthOptions' }, 'open', 130, false, NARROW_MS_CELL),
        coreStack('core-quantity', 'quantity', 'quantity', t('CORE.QUANTITY'), 150),
        coreStack('core-ppu', 'pricePerUnit', 'currency', t('CORE.PRICE_PER_UNIT'), 160),
        /* Wider than the other Core columns by exactly the Invite icon + its margin, so the input box
           itself still lines up with Price Per Unit. */
        coreStack('core-total', 'totalPrice', 'currency', t('CORE.TOTAL_PRICE'), 192, t('CORE.INVITE_SUPPLIER'), true),
      ],
    },

    {
      headerName: t('GROUPS.COMPOSE_EMAIL'),
      groupId: 'email',
      headerClass: 'mssg-grp mssg-grp--email',
      headerGroupComponent: GroupHeader,
      headerGroupComponentParams: { showCheckbox: false },
      marryChildren: true,
      children: [
        emailField(t('EMAIL.TO'), 'emailTo', undefined, 180, true, true),
        emailField(t('EMAIL.CC'), 'emailCc', 'open', 200, false, true),
        emailField(t('EMAIL.BCC'), 'emailBcc', 'open', 200, false, true),
        emailField(t('EMAIL.MESSAGE'), 'emailMessage', 'open', 240, true),
      ],
    },
  ];
}

/** Helper: a Technical Package checkbox + fixed label column. */
function checkLabel(colId: string, checkedField: BoolKey, label: string, show: 'open' | undefined, width = 180, flex = false): ColDef<MultiSubmitRow> {
  return withFill(
    {
      headerName: '',
      colId,
      ...(flex ? { flex: 1, minWidth: width } : { width }),
      ...(show ? { columnGroupShow: show } : {}),
      cellRenderer: CheckLabelCell,
      cellRendererParams: { checkedField, label },
    },
    flagSpec(checkedField),
  );
}

/** Helper: a Supporting Documents checkbox+select column. */
function supportingDoc(
  label: string,
  checkedField: BoolKey,
  valueField: ArrKey,
  optionsKey: OptionsKey,
  show: 'open' | undefined,
  width = 180,
  flex = false,
): ColDef<MultiSubmitRow> {
  return withFill(
    {
      headerName: '',
      colId: `sd-${String(checkedField)}`,
      ...(flex ? { flex: 1, minWidth: width } : { width }),
      cellClass: 'mssg-ms-cell',
      ...(show ? { columnGroupShow: show } : {}),
      cellRenderer: CheckSelectCell,
      cellRendererParams: { checkedField, valueField, optionsKey, placeholder: label },
    },
    checkedListSpec(checkedField, valueField, optionsKey),
  );
}

/** Helper: a Supporting Documents checkbox + fixed label column (Testing Report / Care Layout). */
function sdCheckLabel(colId: string, checkedField: BoolKey, label: string, show: 'open' | undefined, width = 180, flex = false): ColDef<MultiSubmitRow> {
  return withFill(
    {
      headerName: '',
      colId,
      ...(flex ? { flex: 1, minWidth: width } : { width }),
      ...(show ? { columnGroupShow: show } : {}),
      cellRenderer: CheckLabelCell,
      cellRendererParams: { checkedField, label },
    },
    flagSpec(checkedField),
  );
}

/** Helper: a grading-group stacked column (always columnGroupShow=open). */
function gradingCol(
  placeholder: string,
  field: 'gradingGroup' | 'size' | 'length' | 'quantity' | 'uom',
  kind: 'select' | 'input',
  options: { value: string; label: string }[] | undefined,
  width: number,
  optionsKey?: OptionsKey,
  ctxOptionsKey?: 'sizeOptions' | 'lengthOptions' | 'unitOptions',
  cellClass: string = 'mssg-cell-top',
): ColDef<MultiSubmitRow> {
  return withFill(
    {
      headerName: '',
      colId: `rs-grading-${field}`,
      width,
      columnGroupShow: 'open',
      cellClass,
      cellRenderer: GradingStackCell,
      cellRendererParams: { field, kind, options, placeholder, optionsKey, ctxOptionsKey },
    },
    gradingSpec(field, { options, optionsKey, ctxOptionsKey }),
  );
}

/** Helper: a Costing Information plain text (or numeric) column. */
function ciText(
  colId: string,
  valueField: StrKey,
  placeholder: string,
  show: 'open' | undefined,
  width = 160,
  flex = false,
  numeric = false,
): ColDef<MultiSubmitRow> {
  return withFill(
    {
      headerName: '',
      colId,
      ...(flex ? { flex: 1, minWidth: width } : { width }),
      ...(show ? { columnGroupShow: show } : {}),
      cellRenderer: TextInputCell,
      cellRendererParams: { valueField, placeholder, numeric, costingGated: true },
    },
    textSpec(valueField, numeric ? toNumericText : undefined, hasCostingSupplier),
  );
}

/** Helper: a Costing Information single-select column. Options come from a static list, a per-row key, or a page-level ctx key.
 *  Every Costing Information select is gated except the supplier, which is the gate itself. All are clearable (× to unset). */
function ciSelect(
  colId: string,
  valueField: StrKey,
  placeholder: string,
  source: { options?: SelectOption[]; optionsKey?: OptionsKey; ctxOptionsKey?: CtxOptionsKey },
  width = 170,
  costingGated = true,
): ColDef<MultiSubmitRow> {
  return withFill(
    {
      headerName: '',
      colId,
      width,
      columnGroupShow: 'open',
      cellRenderer: PlainSelectCell,
      cellRendererParams: {
        valueField,
        placeholder,
        options: source.options,
        optionsKey: source.optionsKey,
        ctxOptionsKey: source.ctxOptionsKey,
        costingGated,
        clearable: true,
      },
    },
    selectSpec(valueField, optionsFrom(source), undefined, costingGated ? hasCostingSupplier : undefined),
  );
}

/** Helper: a read-only Costing Information display column (Supplier / Currency) — no dropdown. */
function ciDisplay(colId: string, variant: 'supplier' | 'currency', placeholder: string, width = 170): ColDef<MultiSubmitRow> {
  return {
    headerName: '',
    colId,
    width,
    columnGroupShow: 'open',
    cellRenderer: DisplayCell,
    cellRendererParams: { variant, placeholder },
  };
}

/** Helper: a Core Product stacked select column (Colourway/Size/Length). */
function coreSelect(
  colId: string,
  field: CoreMultiKey,
  placeholder: string,
  source: { optionsKey?: OptionsKey; ctxOptionsKey?: CtxOptionsKey },
  show: 'open' | undefined,
  width: number,
  flex = false,
  cellClass: string = 'mssg-ms-cell mssg-cell-top',
): ColDef<MultiSubmitRow> {
  return withFill(
    {
      headerName: '',
      colId,
      ...(flex ? { flex: 1, minWidth: width } : { width }),
      ...(show ? { columnGroupShow: show } : {}),
      cellClass,
      cellRenderer: CoreStackCell,
      cellRendererParams: { field, variant: 'select', optionsKey: source.optionsKey, ctxOptionsKey: source.ctxOptionsKey, placeholder },
    },
    coreSpec(field, source),
  );
}

/** Helper: a Core Product stacked input column (Quantity `+ │ input`, or `£ │ input` price fields).
 *  Pass `inviteLabel` to render an Invite Supplier icon beside each line's control. */
function coreStack(
  colId: string,
  field: CoreKey,
  variant: 'quantity' | 'currency',
  placeholder: string,
  width: number,
  inviteLabel?: string,
  computed = false,
): ColDef<MultiSubmitRow> {
  const colDef: ColDef<MultiSubmitRow> = {
    headerName: '',
    colId,
    width,
    columnGroupShow: 'open',
    cellClass: 'mssg-cell-top',
    cellRenderer: CoreStackCell,
    cellRendererParams: { field, variant, placeholder, computed, ...(inviteLabel ? { showInvite: true, inviteLabel } : {}) },
  };
  return computed ? colDef : withFill(colDef, coreSpec(field));
}

/** Helper: a Compose Email text column. `fill` stretches it to remove trailing empty space. */
function emailField(placeholder: string, valueField: StrKey, show: 'open' | undefined, width: number, fill = false, mention = false): ColDef<MultiSubmitRow> {
  return withFill(
    {
      headerName: '',
      colId: `email-${String(valueField)}`,
      ...(fill ? { flex: 1, minWidth: width, resizable: false } : { width }),
      ...(show ? { columnGroupShow: show } : {}),
      cellRenderer: EmailFieldCell,
      cellRendererParams: { valueField, placeholder, mention },
    },
    textSpec(valueField),
  );
}
